import { useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AccessProvider } from "@/contexts/AccessContext";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import LandingPage from "@/pages/LandingPage";
import AnalyzePage from "@/pages/AnalyzePage";
import PricingPage from "@/pages/PricingPage";
import ComingSoonPage from "@/pages/ComingSoonPage";
import SplashScreen from "@/pages/SplashScreen";
import OnboardingScreen from "@/pages/OnboardingScreen";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import HistoryPage from "@/pages/HistoryPage";
import SettingsPage from "@/pages/SettingsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import DeleteAccountPage from "@/pages/DeleteAccountPage";
import HelpFaqPage from "@/pages/HelpFaqPage";
import WrappedPreviewPage from "@/pages/WrappedPreviewPage";
import DebugExperimentsPage from "@/pages/DebugExperimentsPage";
import AppShell from "@/layouts/AppShell";
import AnalyticsTracker from "@/components/AnalyticsTracker";

function PublicOnlyRoute({ children }) {
  const { isLoading, isAuthenticated } = useAuth();
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F7F9FB]">
        <div className="w-8 h-8 rounded-full border-2 border-[#E5E7EB] border-t-[#2F5BFF] animate-spin"></div>
      </div>
    );
  }
  if (isAuthenticated) return <Navigate to="/app" replace />;
  return children;
}

function AppRoutes() {
  const [relationshipType, setRelationshipType] = useState(null);

  return (
    <Routes>
      {/* Splash & Onboarding */}
      <Route path="/" element={<SplashScreen />} />
      <Route path="/onboarding" element={<OnboardingScreen />} />

      {/* Auth (redirect to /app if already authenticated) */}
      <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
      <Route path="/signup" element={<PublicOnlyRoute><SignupPage /></PublicOnlyRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* App Shell (bottom tabs, requires auth) */}
      <Route path="/app" element={<AppShell />}>
        <Route
          index
          element={<AnalyzePage relationshipType={relationshipType} setRelationshipType={setRelationshipType} />}
        />
        <Route path="history" element={<HistoryPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="help" element={<HelpFaqPage />} />
      </Route>

      {/* Standalone / public utility pages */}
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/delete-account" element={<DeleteAccountPage />} />
      <Route path="/wrapped-preview" element={<WrappedPreviewPage />} />
      <Route path="/debug/experiments" element={<DebugExperimentsPage />} />

      {/* Legacy / web-only pages */}
      <Route path="/web" element={<LandingPage setRelationshipType={setRelationshipType} />} />
      <Route
        path="/analyze"
        element={<AnalyzePage relationshipType={relationshipType} setRelationshipType={setRelationshipType} />}
      />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/coming-soon" element={<ComingSoonPage />} />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function GlobalChrome() {
  const { pathname } = useLocation();
  // Show the announcement banner only on legacy web pages — NOT in the native app shell.
  // The EarlyAccessPopup auto-appearing was bad UX for mobile users.
  const showMarketing = ["/web", "/coming-soon"].some(
    (p) => pathname === p || pathname.startsWith(p + "/")
  );
  if (!showMarketing) return null;
  return (
    <>
      <AnnouncementBanner />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <AccessProvider>
        <BrowserRouter>
          <div className="min-h-screen bg-background">
            <AnalyticsTracker />
            <GlobalChrome />
            <Toaster position="top-center" />
            <AppRoutes />
          </div>
        </BrowserRouter>
      </AccessProvider>
    </AuthProvider>
  );
}

export default App;
